/**
 * Validates a Brazilian CPF (Cadastro de Pessoas Físicas) tax ID number.
 * @param cpf - The CPF string to validate (can be formatted or unformatted)
 * @returns true if the CPF is valid, false otherwise
 */
export function validateCPF(cpf: string): boolean {
  if (!cpf) return false;

  // Remove non-digits
  const digits = cpf.replace(/\D/g, "");

  // Must have 11 digits
  if (digits.length !== 11) return false;

  // Check if all digits are the same (e.g., 111.111.111-11)
  if (digits.split("").every((d) => d === digits[0])) return false;

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let remainder = sum % 11;
  const firstCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  if (firstCheckDigit !== parseInt(digits[9])) return false;

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  remainder = sum % 11;
  const secondCheckDigit = remainder < 2 ? 0 : 11 - remainder;

  return secondCheckDigit === parseInt(digits[10]);
}

/**
 * Formats a CPF string to the standard Brazilian format (000.000.000-00).
 * @param value - The CPF string to format (can be formatted or unformatted)
 * @returns The formatted CPF string
 */
export function formatCPF(value: string): string {
  // Remove non-digits
  const digits = value.replace(/\D/g, "");

  // Apply mask: 000.000.000-00
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  } else if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  } else {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }
}

export function formatCNPJ(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);

  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function validateCNPJ(value: string): boolean {
  const digits = value.replace(/\D/g, "");

  if (digits.length !== 14 || digits.split("").every((digit) => digit === digits[0])) {
    return false;
  }

  const checkDigit = (base: string) => {
    let weight = base.length - 7;
    const sum = [...base].reduce((total, digit) => {
      const result = total + Number(digit) * weight;
      weight = weight === 2 ? 9 : weight - 1;
      return result;
    }, 0);

    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  };

  return (
    checkDigit(digits.slice(0, 12)) === Number(digits[12]) &&
    checkDigit(digits.slice(0, 13)) === Number(digits[13])
  );
}

export function formatCPFOrCNPJ(value: string): string {
  return value.replace(/\D/g, "").length <= 11 ? formatCPF(value) : formatCNPJ(value);
}

/**
 * Formats a CEP (Brazilian ZIP code) string to the standard format (00000-000).
 * @param value - The CEP string to format
 * @returns The formatted CEP string
 */
export function formatCEP(value: string): string {
  // Remove non-digits
  const digits = value.replace(/\D/g, "");

  // Apply mask: 00000-000
  if (digits.length <= 5) {
    return digits;
  } else {
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  }
}

/**
 * Formats a phone number string to the Brazilian format.
 * @param value - The phone string to format
 * @returns The formatted phone string
 */
export function formatPhone(value: string): string {
  // Remove non-digits
  const digits = value.replace(/\D/g, "");

  // Apply mask: (00) 00000-0000 or (00) 0000-0000
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 7) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  } else if (digits.length <= 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  } else {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
}
